import {ChangeDetectionStrategy, Component, DestroyRef, Injectable, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatInput} from '@angular/material/input';
import {TranslateModule} from '@ngx-translate/core';
import {merge, of, from} from 'rxjs';
import {debounceTime, switchMap} from 'rxjs/operators';
import {ElasticSearchResultat, ElasticSearchTreff, IndeksertEntitet, SøkEntitetType, SøkRessursClient, SøkSpesifikasjon} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';

type SøkbarEntitet = {type: SøkEntitetType; navn: string; kontroll: FormControl<boolean>};

@Component({
    selector: 'administrasjon-sok',
    templateUrl: 'administrasjon-sok.html',
    styleUrl: 'administrasjon-sok.scss',
    host: {'data-testid': 'administrasjon-sok'},
    imports: [ReactiveFormsModule, MatCheckbox, MatInput, TranslateModule],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonSokKomponent {
    static ajsSelector = 'administrasjonSok';

    readonly sokestreng = new FormControl('', {nonNullable: true});
    readonly sokbareEntiteter: SøkbarEntitet[] = [
        {type: SøkEntitetType.bruker, navn: 'Brukere', kontroll: new FormControl(true, {nonNullable: true})},
        {type: SøkEntitetType.medlem, navn: 'Medlemmer', kontroll: new FormControl(true, {nonNullable: true})},
        {type: SøkEntitetType.gruppe, navn: 'Grupper', kontroll: new FormControl(true, {nonNullable: true})}
    ];
    readonly viserValg = signal(false);
    readonly viserResultater = signal(false);
    readonly resultat = signal<ElasticSearchResultat | null>(null);
    readonly indekserteEntiteter = IndeksertEntitet;
    readonly navigerTilTreff = output<ElasticSearchTreff<any>>();

    constructor(private avhengigheter: AdministrasjonSokAvhengigheter, destroyRef: DestroyRef) {
        merge(this.sokestreng.valueChanges, ...this.sokbareEntiteter.map(entitet => entitet.kontroll.valueChanges))
          .pipe(
            debounceTime(200),
            switchMap(() => {
                if (this.sokestreng.value.trim().length < 3) return of(null);
                return from(this.avhengigheter.sok(this.sokespesifikasjon()));
            }),
            takeUntilDestroyed(destroyRef)
          )
          .subscribe(resultat => this.resultat.set(resultat));
    }

    visResultater(): void {
        this.viserResultater.set(true);
    }

    skjulResultater(): void {
        this.viserResultater.set(false);
        this.viserValg.set(false);
    }

    byttVisningAvValg(): void {
        if (this.valgErAktive()) return;
        this.viserValg.update(vises => !vises);
    }

    nullstillValg(): void {
        this.sokbareEntiteter.forEach(entitet => entitet.kontroll.setValue(true));
        this.viserValg.set(false);
    }

    byttAlleEntiteter(): void {
        const skalVelges = this.sokbareEntiteter.some(entitet => !entitet.kontroll.value);
        this.sokbareEntiteter.forEach(entitet => entitet.kontroll.setValue(skalVelges));
    }

    velgTreff(treff: ElasticSearchTreff<any>): void {
        this.navigerTilTreff.emit(treff);
        this.skjulResultater();
    }

    handterTastehendelse(hendelse: KeyboardEvent): void {
        if (hendelse.key === 'Escape') this.skjulResultater();
    }

    alleEntiteterValgt(): boolean {
        return this.sokbareEntiteter.every(entitet => entitet.kontroll.value);
    }

    ingenEntiteterValgt(): boolean {
        return this.sokbareEntiteter.every(entitet => !entitet.kontroll.value);
    }

    valgErAktive(): boolean {
        return !this.alleEntiteterValgt();
    }

    treffTittel(treff: ElasticSearchTreff<any>): string {
        switch (treff._type) {
            case IndeksertEntitet.gruppe: return `${treff._source.organisasjonsnavn} - ${treff._source.tittel}`;
            case IndeksertEntitet.medlem: return treff._source.navn;
            case IndeksertEntitet.bruker: return treff._source.navn;
            default: return 'Ukjent treff';
        }
    }

    treffType(treff: ElasticSearchTreff<any>): string {
        switch (treff._type) {
            case IndeksertEntitet.gruppe: return 'Gruppe';
            case IndeksertEntitet.medlem: return 'Medlem';
            case IndeksertEntitet.bruker: return 'Bruker';
            default: return 'Ukjent';
        }
    }

    treffBeskrivelse(treff: ElasticSearchTreff<any>): string {
        switch (treff._type) {
            case IndeksertEntitet.gruppe: return `Avtale: ${treff._source.avtalenavn}`;
            case IndeksertEntitet.medlem: return `${treff._source.epost} – ${treff._source.gruppetittel} i ${treff._source.gruppeorganisasjonsnavn}`;
            case IndeksertEntitet.bruker: return treff._source.epost;
            default: return '';
        }
    }

    private sokespesifikasjon(): SøkSpesifikasjon {
        return {
            soekestreng: this.sokestreng.value,
            periode: null,
            inkluderteEntiteter: this.sokbareEntiteter.filter(entitet => entitet.kontroll.value).map(entitet => entitet.type),
            inkluderteGrupper: []
        };
    }
}

@Injectable({providedIn: 'root'})
export class AdministrasjonSokAvhengigheter {
    constructor(private ressurs: SøkRessursClient) {}

    sok(spesifikasjon: SøkSpesifikasjon): Promise<ElasticSearchResultat> {
        return this.ressurs.adminSøk(spesifikasjon).somPromise();
    }
}
